import { Typography } from "@mui/material";
import type { ReactNode } from "react";
import { SystemDropdown } from "../../Inputs/SystemDropDown";
import type { DropdownItem, Extracted } from "../../Inputs/SystemDropDown/types";
import { BORDERLESS_SELECT_SX } from "./configTableSx";

/** Shared red helper caption shown beneath a cell (e.g. "choose All or specific"). Size comes from the `caption` variant. */
const ERROR_CAPTION_SX = { color: "error.main", mt: 0.5, display: "block" } as const;

type Props<T> = {
	/** Already-built dropdown items (value/label/selectedLabel/color). */
	items: DropdownItem<T>[];
	selectedItems: T[];
	onChangeMultiple: (selected: T[]) => void;
	valueToString?: (v: T) => string;
	/** Accessible name when the cell has no visible label (break-rules / overtime tables). */
	ariaLabel?: string;
	/** Stable DOM id (the hours-rate table keys its cells by id instead of aria-label). */
	id?: string;
	/** Label for the "All" pseudo-option in the menu (omit to hide it). */
	allOption?: string;
	disabled?: boolean;
	/** When set, renders the red helper caption beneath the field. */
	errorText?: ReactNode;
	dataTestId?: string;
};

/**
 * Borderless multi-select cell shared by the dense config tables (Break Rules, Overtime,
 * Hours Rate on Team → Wage Settings; Auto-approve on Department Configuration). Owns the
 * `SystemDropdown` chrome + "choose All or specific" caption; each table supplies its own items,
 * selection and normalization.
 */
export const ConfigMultiSelectCell = <T,>({
	items,
	selectedItems,
	onChangeMultiple,
	valueToString = (v) => String(v),
	ariaLabel,
	id,
	allOption,
	disabled,
	errorText,
	dataTestId,
}: Props<T>) => (
	<>
		<SystemDropdown<T>
			fullWidth
			multiple
			formControlSx={{ width: "100%" }}
			selectSx={BORDERLESS_SELECT_SX}
			aria-label={ariaLabel}
			id={id}
			allOption={allOption}
			disabled={disabled}
			// `Extracted<T>` reduces to `T` for our plain value types, but the generic wrapper
			// can't prove it — cast at the boundary only; callers stay fully typed on `T`.
			items={items as DropdownItem<Extracted<T>>[]}
			selectedItems={selectedItems as Extracted<T>[]}
			valueToString={valueToString as (v: Extracted<T>) => string}
			onChangeMultiple={onChangeMultiple as (v: Extracted<T>[]) => void}
			data-testid={dataTestId}
		/>
		{errorText ? (
			<Typography variant="caption" sx={ERROR_CAPTION_SX} data-testid={dataTestId ? `${dataTestId}-error` : undefined}>
				{errorText}
			</Typography>
		) : null}
	</>
);
